import styled from 'styled-components';
import {ComboBox, Text} from '@forecast-it/design-system';
import {useForecastProjects, type ForecastProjectOption} from '../hooks/useForecastProjects';

interface ProjectDropdownProps {
	value: string | null;
	onChange: (projectId: string | null) => void;
	/** Visible label above the field; it is also the input's accessible name. */
	label?: string;
	disabled?: boolean;
	'data-cy'?: string;
}

const Hint = styled.div`
	margin-top: 4px;
`;

/**
 * Type-to-filter project picker for ticket flows. The list comes from
 * useForecastProjects, the same source as the Add task modal's Project field, so
 * its contents, labels and order match that field. It opens to the full list on
 * focus, and typing filters it.
 */
export function ProjectDropdown({
	value,
	onChange,
	label = 'Project',
	disabled = false,
	'data-cy': dataCy = 'project-dropdown',
}: ProjectDropdownProps) {
	const {projects, loading, error} = useForecastProjects();

	return (
		<>
			{/* The design-system ComboBox takes its selection through `value` and reports
			    it through `onChange`; it maps those onto react-aria's selectedKey /
			    onSelectionChange itself, so passing those names directly clears the
			    selection. `disabled` only styles the input; `isDisabled` also locks the
			    button that opens the list. */}
			<ComboBox
				data-cy={dataCy}
				label={label}
				width="flex"
				menuTrigger="focus"
				placeholder={loading ? 'Loading projects…' : 'Select a project…'}
				value={value}
				defaultItems={projects}
				disabled={disabled || loading}
				isDisabled={disabled || loading}
				onChange={(key: string | null) => onChange(key || null)}
			>
				{(item: ForecastProjectOption) => (
					<ComboBox.Item key={item.id} textValue={item.name}>
						<Text noWrap>{item.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			{!loading && error && (
				<Hint>
					<Text>Couldn't load projects.</Text>
				</Hint>
			)}
			{!loading && !error && projects.length === 0 && (
				<Hint>
					<Text>No projects available.</Text>
				</Hint>
			)}
		</>
	);
}
